import React, { useEffect, useMemo, useState } from "react";
import mixpanel from "mixpanel-browser";
import styled from "styled-components";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  ReferenceArea,
} from "recharts";
import { useTranslation } from "react-i18next";
import { Clock, Ban, CircleDot, TrendingUp } from "lucide-react";

import heatwaveSummaryCsv from "../../../data/HeatwaveJune2026/heatwave_summary_by_country_date.csv";
import heatwaveCountrySensitivityCsv from "../../../data/HeatwaveJune2026/heatwave_country_sensitivity_summary.csv";
import HeatwaveCountryFilter from "./HeatwaveCountryFilter";
import HeatwaveDiveDeepKpis from "./HeatwaveDiveDeepKpis";
import {
  ChartCountry,
  CHART_COUNTRIES,
  DiveDeepChartMetric,
  EXCLUDED_DATES,
  parseHeatwaveSensitivityCsv,
  parseHeatwaveSummaryCsv,
  roundDownTo5,
  roundUpTo5,
  secondsToMinutes,
  SensitivitySummaryRow,
  SummaryRow,
} from "./heatwaveChartShared";

const colors = {
  textDark: "#0f2744",
  textMid: "#2d4a6a",
  textMuted: "#5a7a94",
  cardBorder: "rgba(255, 255, 255, 0.95)",
  skyDeep: "#4A9FD4",
  skyDot: "rgba(74, 159, 212, 0.42)",
  skyLineMuted: "rgba(74, 159, 212, 0.2)",
  orange500: "#F97316",
  orangeDot: "rgba(249, 115, 22, 0.42)",
  orangeLineMuted: "rgba(249, 115, 22, 0.2)",
};

const HEAT_THRESHOLD_C = 30;

const ROLLING_WINDOW_DAYS = 5;

type TimeSeriesPoint = {
  date: string;
  dateLabel: string;
  delayMin: number;
  delayMinAvg5: number;
  cancelRatePct: number;
  cancelRatePctAvg5: number;
  temperature: number;
  temperatureAvg5: number;
};

const ChartWrapper = styled.div`
  width: 100%;
  height: 22rem;
  min-height: 22rem;

  @media (min-width: 768px) {
    height: 26rem;
    min-height: 26rem;
  }
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 0.2rem;
  font-size: 0.875rem;
  color: ${colors.textMuted};
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendLine = styled.span<{ $color: string }>`
  display: inline-block;
  width: 2rem;
  height: 0;
  border-top: 3px solid ${({ $color }) => $color};
`;

const LegendDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const ChartDisclaimer = styled.p`
  margin: 0 auto 1rem;
  max-width: 42rem;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: ${colors.textMuted};
  text-align: center;
`;

const MetricSwitch = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2.25rem;
  flex-wrap: wrap;
`;

const ChartEmphasisSwitch = styled(MetricSwitch)`
  margin-top: 0;
  margin-bottom: 1rem;
`;

const MetricSwitchButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid
    ${({ $active }) => ($active ? "rgba(74, 159, 212, 0.7)" : "rgba(126, 200, 227, 0.45)")};
  background: ${({ $active }) =>
    $active ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.55)"};
  color: ${({ $active }) => ($active ? colors.textDark : colors.textMuted)};
  font-size: 0.9375rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  padding: 0.625rem 1.125rem;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active ? "0 2px 8px rgba(74, 159, 212, 0.2)" : "none"};
  line-height: 1.2;
  white-space: nowrap;

  &:hover {
    color: ${colors.textDark};
    border-color: rgba(74, 159, 212, 0.7);
  }
`;

const COUNTRIES_WITH_DISCLAIMER = new Set<ChartCountry>(["DE", "FR", "HU"]);

const TooltipCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid ${colors.cardBorder};
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 8px 20px rgba(74, 159, 212, 0.2);
  font-size: 0.875rem;
  color: ${colors.textDark};
  text-align: left;
  min-width: 10rem;
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  &:not(:last-child) {
    margin-bottom: 0.35rem;
  }

  span:first-child {
    color: ${colors.textMuted};
  }

  span:last-child {
    font-weight: 600;
    color: ${colors.textDark};
  }
`;

function formatServiceDate(dateStr: string, locale: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(
    new Date(year, month - 1, day)
  );
}

function formatAxisDate(dateStr: string, locale: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(
    new Date(year, month - 1, day)
  );
}

function rollingAverage(values: number[], window: number): number[] {
  return values.map((_, index) => {
    const start = Math.max(0, index - window + 1);
    const slice = values.slice(start, index + 1);
    return slice.reduce((sum, value) => sum + value, 0) / slice.length;
  });
}

function buildTimeSeriesPoints(rows: SummaryRow[]): TimeSeriesPoint[] {
  const sorted = [...rows]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((row) => ({
      date: row.date,
      dateLabel: row.date,
      delayMin: secondsToMinutes(row.avg_delay_s),
      cancelRatePct: row.cancel_rate * 100,
      temperature: row.max_temperature_c,
    }));

  const delayMins = sorted.map((point) => point.delayMin);
  const cancelRates = sorted.map((point) => point.cancelRatePct);
  const temperatures = sorted.map((point) => point.temperature);
  const delayMinAvg5 = rollingAverage(delayMins, ROLLING_WINDOW_DAYS);
  const cancelRatePctAvg5 = rollingAverage(cancelRates, ROLLING_WINDOW_DAYS);
  const temperatureAvg5 = rollingAverage(temperatures, ROLLING_WINDOW_DAYS);

  return sorted.map((point, index) => ({
    ...point,
    delayMinAvg5: delayMinAvg5[index],
    cancelRatePctAvg5: cancelRatePctAvg5[index],
    temperatureAvg5: temperatureAvg5[index],
  }));
}

function computeDelayDomain(points: TimeSeriesPoint[]): [number, number] {
  if (points.length === 0) return [0, 8];
  const values = points.flatMap((point) => [point.delayMin, point.delayMinAvg5]);
  const maxDelay = Math.max(...values);
  return [0, Math.max(4, Math.ceil(maxDelay * 1.12))];
}

function computeCancellationDomain(points: TimeSeriesPoint[]): [number, number] {
  if (points.length === 0) return [0, 5];
  const values = points.flatMap((point) => [point.cancelRatePct, point.cancelRatePctAvg5]);
  const maxCancel = Math.max(...values);
  const paddedMax = Math.ceil(maxCancel * 1.15 * 10) / 10;
  return [0, Math.max(1, paddedMax)];
}

function computeTemperatureAxis(points: TimeSeriesPoint[]): {
  domain: [number, number];
  ticks: number[];
} {
  const fallback = {
    domain: [10, 35] as [number, number],
    ticks: [10, 15, 20, 25, 30, 35],
  };
  if (points.length === 0) return fallback;

  const temps = points.flatMap((point) => [point.temperature, point.temperatureAvg5]);
  let min = roundDownTo5(Math.min(...temps));
  let max = roundUpTo5(Math.max(...temps));
  min = Math.min(min, HEAT_THRESHOLD_C);
  max = Math.max(max, HEAT_THRESHOLD_C);
  if (max <= min) max = min + 5;

  const ticks: number[] = [];
  for (let tick = min; tick <= max; tick += 5) {
    ticks.push(tick);
  }
  if (!ticks.includes(HEAT_THRESHOLD_C)) {
    ticks.push(HEAT_THRESHOLD_C);
    ticks.sort((a, b) => a - b);
  }

  return { domain: [min, max], ticks };
}

function pickXAxisTicks(dates: string[]): string[] {
  if (dates.length <= 8) return dates;
  const step = Math.ceil(dates.length / 8);
  return dates.filter((_, index) => index % step === 0 || index === dates.length - 1);
}

type TimeSeriesTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: TimeSeriesPoint }>;
  metric: DiveDeepChartMetric;
};

const TimeSeriesTooltip = ({ active, payload, metric }: TimeSeriesTooltipProps) => {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as TimeSeriesPoint | undefined;
  if (!point) return null;

  const locale = i18n.language;
  const fmtDec = (v: number) =>
    v.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const primaryLabel =
    metric === "delay"
      ? t("heatwave.diveDeep.legendDelay")
      : t("heatwave.diveDeep.legendCancellation");
  const primaryAvgLabel =
    metric === "delay"
      ? t("heatwave.diveDeep.legendDelayAvg5")
      : t("heatwave.diveDeep.legendCancellationAvg5");
  const primaryValue =
    metric === "delay" ? `${fmtDec(point.delayMin)} min` : `${fmtDec(point.cancelRatePct)}%`;
  const primaryAvgValue =
    metric === "delay"
      ? `${fmtDec(point.delayMinAvg5)} min`
      : `${fmtDec(point.cancelRatePctAvg5)}%`;

  return (
    <TooltipCard>
      <TooltipRow>
        <span>{t("heatwave.diveDeep.tooltip.date")}</span>
        <span>{formatServiceDate(point.date, locale)}</span>
      </TooltipRow>
      <TooltipRow>
        <span>{primaryLabel}</span>
        <span>{primaryValue}</span>
      </TooltipRow>
      <TooltipRow>
        <span>{primaryAvgLabel}</span>
        <span>{primaryAvgValue}</span>
      </TooltipRow>
      <TooltipRow>
        <span>{t("heatwave.diveDeep.legendTemperature")}</span>
        <span>{fmtDec(point.temperature)}°C</span>
      </TooltipRow>
      <TooltipRow>
        <span>{t("heatwave.diveDeep.legendTemperatureAvg5")}</span>
        <span>{fmtDec(point.temperatureAvg5)}°C</span>
      </TooltipRow>
    </TooltipCard>
  );
};

type ChartEmphasis = "lines" | "dots";

interface DelayTemperatureTimeSeriesProps {
  visible?: boolean;
}

const DelayTemperatureTimeSeries: React.FC<DelayTemperatureTimeSeriesProps> = ({
  visible = true,
}) => {
  const { t, i18n } = useTranslation();
  const [allRows, setAllRows] = useState<SummaryRow[]>([]);
  const [sensitivityRows, setSensitivityRows] = useState<SensitivitySummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState<ChartCountry>("ALL");
  const [metric, setMetric] = useState<DiveDeepChartMetric>("delay");
  const [chartEmphasis, setChartEmphasis] = useState<ChartEmphasis>("lines");

  useEffect(() => {
    const load = async () => {
      try {
        const [summaryResponse, sensitivityResponse] = await Promise.all([
          fetch(heatwaveSummaryCsv),
          fetch(heatwaveCountrySensitivityCsv),
        ]);

        if (summaryResponse.ok) {
          const text = await summaryResponse.text();
          const parsed = parseHeatwaveSummaryCsv(text).filter(
            (row) =>
              !EXCLUDED_DATES.has(row.date) &&
              !isNaN(row.avg_delay_s) &&
              !isNaN(row.cancel_rate) &&
              !isNaN(row.max_temperature_c)
          );
          setAllRows(parsed);
        } else {
          setAllRows([]);
        }

        if (sensitivityResponse.ok) {
          const text = await sensitivityResponse.text();
          setSensitivityRows(parseHeatwaveSensitivityCsv(text));
        } else {
          setSensitivityRows([]);
        }
      } catch {
        setAllRows([]);
        setSensitivityRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const availableCountries = useMemo(() => {
    const codes = new Set(allRows.map((row) => row.stopcountry));
    return CHART_COUNTRIES.filter((code) => codes.has(code));
  }, [allRows]);

  const rows = useMemo(
    () => allRows.filter((row) => row.stopcountry === country),
    [allRows, country]
  );

  const sensitivityRow = useMemo(
    () => sensitivityRows.find((row) => row.stopcountry === country),
    [sensitivityRows, country]
  );

  const chartData = useMemo(() => buildTimeSeriesPoints(rows), [rows]);
  const hidePrimaryMetric = country === "HU" && metric === "cancellation";
  const primaryMetricKey = metric === "delay" ? "delayMin" : "cancelRatePct";
  const primaryAvgKey = metric === "delay" ? "delayMinAvg5" : "cancelRatePctAvg5";
  const primaryDomain = useMemo(
    () =>
      metric === "delay"
        ? computeDelayDomain(chartData)
        : computeCancellationDomain(chartData),
    [chartData, metric]
  );
  const primaryAxisLabel =
    metric === "delay"
      ? t("heatwave.diveDeep.axisDelay")
      : t("heatwave.diveDeep.axisCancellation");
  const primaryLegendLabel =
    metric === "delay"
      ? t("heatwave.diveDeep.legendDelay")
      : t("heatwave.diveDeep.legendCancellation");
  const primaryAvgLegendLabel =
    metric === "delay"
      ? t("heatwave.diveDeep.legendDelayAvg5")
      : t("heatwave.diveDeep.legendCancellationAvg5");
  const fmtDec = (v: number) =>
    v.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const formatPrimaryTick = (value: number) =>
    metric === "delay" ? fmtDec(Number(value)) : `${fmtDec(Number(value))}%`;
  const fmtAxisDate = (dateStr: string) => formatAxisDate(dateStr, i18n.language);
  const temperatureAxis = useMemo(() => computeTemperatureAxis(chartData), [chartData]);
  const temperatureDomain = temperatureAxis.domain;
  const temperatureTicks = temperatureAxis.ticks;
  const xAxisTicks = useMemo(
    () => pickXAxisTicks(chartData.map((point) => point.date)),
    [chartData]
  );
  // Find consecutive date runs where temperature >= threshold → full-height vertical bands
  const hotDateRanges = useMemo(() => {
    const ranges: Array<{ x1: string; x2: string }> = [];
    let rangeStart: string | null = null;
    let rangeEnd: string | null = null;
    for (const point of chartData) {
      if (point.temperature >= HEAT_THRESHOLD_C) {
        if (rangeStart === null) rangeStart = point.date;
        rangeEnd = point.date;
      } else {
        if (rangeStart !== null && rangeEnd !== null) {
          ranges.push({ x1: rangeStart, x2: rangeEnd });
        }
        rangeStart = null;
        rangeEnd = null;
      }
    }
    if (rangeStart !== null && rangeEnd !== null) {
      ranges.push({ x1: rangeStart, x2: rangeEnd });
    }
    return ranges;
  }, [chartData]);

  const emphasizeDots = chartEmphasis === "dots";
  const primaryDotColor = emphasizeDots ? colors.skyDeep : colors.skyDot;
  const primaryLineColor = emphasizeDots ? colors.skyLineMuted : colors.skyDeep;
  const temperatureDotColor = emphasizeDots ? colors.orange500 : colors.orangeDot;
  const temperatureLineColor = emphasizeDots ? colors.orangeLineMuted : colors.orange500;

  if (loading) {
    return (
      <ChartWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center", color: colors.textMuted }}>
        {t("heatwave.diveDeep.loading")}
      </ChartWrapper>
    );
  }

  if (!allRows.length) {
    return (
      <ChartWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center", color: colors.textMuted }}>
        {t("heatwave.diveDeep.loadError")}
      </ChartWrapper>
    );
  }

  return (
    <div style={{ opacity: visible ? 1 : 0.4, transition: "opacity 0.8s ease" }}>
      <HeatwaveCountryFilter
        country={country}
        onChange={setCountry}
        availableCountries={availableCountries}
      />
      <MetricSwitch>
        <MetricSwitchButton
          type="button"
          $active={metric === "delay"}
          onClick={() => { mixpanel.track("Heatwave Chart Metric Switched", { metric: "delay" }); setMetric("delay"); }}
          aria-pressed={metric === "delay"}
        >
          <Clock size={18} color={colors.skyDeep} strokeWidth={2} aria-hidden />
          {t("heatwave.diveDeep.metricSwitch.delays")}
        </MetricSwitchButton>
        <MetricSwitchButton
          type="button"
          $active={metric === "cancellation"}
          onClick={() => { mixpanel.track("Heatwave Chart Metric Switched", { metric: "cancellation" }); setMetric("cancellation"); }}
          aria-pressed={metric === "cancellation"}
        >
          <Ban size={18} color={colors.orange500} strokeWidth={2} aria-hidden />
          {t("heatwave.diveDeep.metricSwitch.cancellations")}
        </MetricSwitchButton>
      </MetricSwitch>
      <HeatwaveDiveDeepKpis summaryRow={sensitivityRow} metric={metric} />
      {COUNTRIES_WITH_DISCLAIMER.has(country) && (
        <ChartDisclaimer>{t(`heatwave.diveDeep.countryDisclaimer.${country}`)}</ChartDisclaimer>
      )}
      <ChartEmphasisSwitch>
        <MetricSwitchButton
          type="button"
          $active={chartEmphasis === "lines"}
          onClick={() => { mixpanel.track("Heatwave Chart Emphasis Switched", { emphasis: "lines" }); setChartEmphasis("lines"); }}
          aria-pressed={chartEmphasis === "lines"}
        >
          <TrendingUp size={18} color={colors.skyDeep} strokeWidth={2} aria-hidden />
          {t("heatwave.diveDeep.chartEmphasis.lines")}
        </MetricSwitchButton>
        <MetricSwitchButton
          type="button"
          $active={chartEmphasis === "dots"}
          onClick={() => { mixpanel.track("Heatwave Chart Emphasis Switched", { emphasis: "dots" }); setChartEmphasis("dots"); }}
          aria-pressed={chartEmphasis === "dots"}
        >
          <CircleDot size={18} color={colors.skyDeep} strokeWidth={2} aria-hidden />
          {t("heatwave.diveDeep.chartEmphasis.dots")}
        </MetricSwitchButton>
      </ChartEmphasisSwitch>
      <ChartWrapper>
        {chartData.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: colors.textMuted,
            }}
          >
            {t("heatwave.diveDeep.loadError")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              key={`${country}-${metric}`}
              data={chartData}
              margin={{ top: 12, right: 48, bottom: 36, left: 8 }}
            >
              {hotDateRanges.map(({ x1, x2 }) => (
                <ReferenceArea
                  key={`heat-${x1}-${x2}`}
                  x1={x1}
                  x2={x2}
                  yAxisId="primary"
                  fill="rgba(255, 115, 30, 0.10)"
                  strokeOpacity={0}
                  ifOverflow="hidden"
                  style={{ pointerEvents: "none" }}
                />
              ))}
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(74, 159, 212, 0.22)" />
              <XAxis
                dataKey="date"
                ticks={xAxisTicks}
                tickFormatter={fmtAxisDate}
                tick={{ fill: colors.textMuted, fontSize: 12 }}
                tickLine={{ stroke: colors.textMuted }}
                axisLine={{ stroke: "rgba(74, 159, 212, 0.4)" }}
                minTickGap={24}
              />
              <YAxis
                yAxisId="primary"
                orientation="left"
                domain={primaryDomain}
                tick={{ fill: colors.skyDeep, fontSize: 12 }}
                tickLine={{ stroke: colors.skyDeep }}
                axisLine={{ stroke: "rgba(74, 159, 212, 0.4)" }}
                tickFormatter={formatPrimaryTick}
                width={48}
                label={{
                  value: primaryAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  fill: colors.skyDeep,
                  fontSize: 13,
                }}
              />
              <YAxis
                yAxisId="temperature"
                orientation="right"
                domain={temperatureDomain}
                ticks={temperatureTicks}
                tick={{ fill: colors.orange500, fontSize: 12 }}
                tickLine={{ stroke: colors.orange500 }}
                axisLine={{ stroke: "rgba(249, 115, 22, 0.4)" }}
                tickFormatter={(value) => `${value}°`}
                width={48}
                label={{
                  value: t("heatwave.diveDeep.axisTemperature"),
                  angle: 90,
                  position: "insideRight",
                  fill: colors.orange500,
                  fontSize: 13,
                }}
              />
              <Tooltip
                content={({ active, payload }) => (
                  <TimeSeriesTooltip active={active} payload={payload} metric={metric} />
                )}
              />
              {!hidePrimaryMetric && (
                <Line
                  yAxisId="primary"
                  type="monotone"
                  dataKey={primaryAvgKey}
                  stroke={primaryLineColor}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={visible}
                  animationDuration={1200}
                />
              )}
              <Line
                yAxisId="temperature"
                type="monotone"
                dataKey="temperatureAvg5"
                stroke={temperatureLineColor}
                strokeWidth={2.5}
                dot={false}
                activeDot={false}
                isAnimationActive={visible}
                animationDuration={1200}
              />
              {!hidePrimaryMetric && (
                <Line
                  yAxisId="primary"
                  type="monotone"
                  dataKey={primaryMetricKey}
                  stroke="none"
                  dot={{ r: 3, fill: primaryDotColor, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: colors.skyDeep, strokeWidth: 0 }}
                  isAnimationActive={false}
                  legendType="none"
                />
              )}
              <Line
                yAxisId="temperature"
                type="monotone"
                dataKey="temperature"
                stroke="none"
                dot={{ r: 3, fill: temperatureDotColor, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: colors.orange500, strokeWidth: 0 }}
                isAnimationActive={false}
                legendType="none"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>
      <LegendRow>
        {!hidePrimaryMetric && (
          <LegendItem>
            <LegendDot $color={primaryDotColor} />
            <span>{primaryLegendLabel}</span>
          </LegendItem>
        )}
        {!hidePrimaryMetric && (
          <LegendItem>
            <LegendLine $color={primaryLineColor} />
            <span>{primaryAvgLegendLabel}</span>
          </LegendItem>
        )}
        <LegendItem>
          <LegendDot $color={temperatureDotColor} />
          <span>{t("heatwave.diveDeep.legendTemperature")}</span>
        </LegendItem>
        <LegendItem>
          <LegendLine $color={temperatureLineColor} />
          <span>{t("heatwave.diveDeep.legendTemperatureAvg5")}</span>
        </LegendItem>
      </LegendRow>
    </div>
  );
};

export default DelayTemperatureTimeSeries;
